import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, Subject, map, tap } from "rxjs";
import { environment } from "src/environments/environment";
import {
    AssessmentTaskApiError, requireAssessmentOption, requireAssigneeOption,
    requireCommentDeleteReceipt, requireCommentMutationReceipt, requireRequirementOption,
    requireStatusOption, requireTaskApiData, requireTaskComment, requireTaskCount,
    requireTaskCreationOptions, requireTaskDeleteReceipt, requireTaskEditOptions,
    requireTaskMutationReceipt,
    requireTaskPage, requireTaskStatusCounts, requireTaskSummary
} from "./assessment_tasks.dto";
import type { AssessmentTaskApiResponse } from "./assessment_tasks.dto";
import type {
    AssessmentTaskAssessmentOption, AssessmentTaskAssigneeOption, AssessmentTaskComment,
    AssessmentTaskCommentDeleteReceipt, AssessmentTaskCommentMutationReceipt,
    AssessmentTaskCount, AssessmentTaskCreateInput, AssessmentTaskCreationOptions,
    AssessmentTaskCreationOptionsQuery, AssessmentTaskDeleteReceipt, AssessmentTaskDetail,
    AssessmentTaskEditInput, AssessmentTaskEditOptions, AssessmentTaskListFilters,
    AssessmentTaskMutationReceipt, AssessmentTaskRequirementRef,
    AssessmentTaskOptionQuery, AssessmentTaskPage, AssessmentTaskPagination, AssessmentTaskRequirementOption,
    AssessmentTaskStatus, AssessmentTaskStatusOption, AssessmentTaskSummary
} from "./assessment_tasks.types";

export interface AssessmentTaskMutationEvent {
    taskId: number;
    // Null means callers should refresh any currently displayed assessment scope.
    productLicenseId: number | null;
    // Null means the affected requirements are unknown; consumers must not discard the event.
    affectedRequirements: readonly AssessmentTaskRequirementRef[] | null;
    kind: "task" | "comment" | "delete";
}

function requireInputId(value: number, name: string): number {
    if (!Number.isSafeInteger(value) || value <= 0) throw new Error(`A valid ${name} is required.`);
    return value;
}

function requireInputRevision(value: number): number {
    if (!Number.isSafeInteger(value) || value < 0) throw new Error("A valid revision is required.");
    return value;
}

function pageParams(query: AssessmentTaskPagination = {}): HttpParams {
    let params = new HttpParams();
    if (query.page !== undefined) params = params.set("page", String(requireInputId(query.page, "page")));
    if (query.pageSize !== undefined) params = params.set("pageSize", String(requireInputId(query.pageSize, "page size")));
    return params;
}

function optionParams(query: AssessmentTaskOptionQuery = {}): HttpParams {
    let params = pageParams(query);
    if (query.search !== undefined) params = params.set("search", query.search);
    return params;
}

function taskListParams(filters: AssessmentTaskListFilters): HttpParams {
    let params = pageParams(filters);
    if (filters.sort !== undefined) params = params.set("sort", filters.sort);
    if (filters.search !== undefined) params = params.set("search", filters.search);
    if (filters.taskId !== undefined) params = params.set("taskId", String(requireInputId(filters.taskId, "task ID")));
    if (filters.assignedToMe !== undefined) params = params.set("assignedToMe", String(filters.assignedToMe));
    if (filters.requirement) {
        params = params.set("sectionKey", filters.requirement.sectionKey)
            .set("requirementKey", filters.requirement.requirementKey);
    }
    for (const [name, values] of [
        ["assigneeUserIds", filters.assigneeUserIds],
        ["assessmentIds", filters.assessmentIds]
    ] as const) {
        if (values?.length) {
            params = params.set(name, values.map((id) => requireInputId(id, name)).join(","));
        }
    }
    if (filters.statuses?.length) params = params.set("statuses", filters.statuses.join(","));
    return params;
}

function creationOptionsParams(query: AssessmentTaskCreationOptionsQuery): HttpParams {
    let params = new HttpParams();
    if (query.assigneeUserId !== undefined) params = params.set("assigneeUserId", String(requireInputId(query.assigneeUserId, "assignee user ID")));
    for (const name of ["requirementOffset", "assigneeOffset"] as const) {
        const value = query[name];
        if (value !== undefined) {
            if (!Number.isSafeInteger(value) || value < 0) throw new Error(`A valid ${name} is required.`);
            params = params.set(name, String(value));
        }
    }
    for (const name of ["requirementLimit", "assigneeLimit"] as const) {
        const value = query[name];
        if (value !== undefined) params = params.set(name, String(requireInputId(value, name)));
    }
    for (const name of ["requirementSearch", "assigneeSearch"] as const) {
        if (query[name] !== undefined) params = params.set(name, query[name]!);
    }
    return params;
}

@Injectable({ providedIn: "root" })
export class AssessmentTasksService {
    private readonly endpoint = `${environment.baseurl.replace(/\/$/, "")}/assessment-tasks`;
    private readonly mutationEvents = new Subject<AssessmentTaskMutationEvent>();

    constructor(private readonly http: HttpClient) {}

    watchMutations(): Observable<AssessmentTaskMutationEvent> {
        return this.mutationEvents.asObservable();
    }

    listTasks(filters: AssessmentTaskListFilters = {}): Observable<AssessmentTaskPage<AssessmentTaskSummary>> {
        const params = taskListParams(filters);
        const taskId = filters.taskId;
        const assessmentIds = filters.assessmentIds?.length ? new Set(filters.assessmentIds) : null;
        return this.get(this.endpoint, (data) => {
            const page = requireTaskPage(data, requireTaskSummary);
            if (page.items.some((task) =>
                (taskId !== undefined && task.id !== taskId) ||
                (assessmentIds !== null && !assessmentIds.has(task.productLicenseId)))) {
                throw new AssessmentTaskApiError("Task list response changed scope.");
            }
            return page;
        }, params);
    }

    getTask(taskId: number): Observable<AssessmentTaskDetail> {
        const id = requireInputId(taskId, "task ID");
        return this.get(`${this.endpoint}/${id}`, (data) => {
            const task = requireTaskSummary(data);
            if (task.id !== id) throw new AssessmentTaskApiError("Task response changed target.");
            return task;
        });
    }

    listComments(taskId: number, query: AssessmentTaskPagination = {}): Observable<AssessmentTaskPage<AssessmentTaskComment>> {
        const id = requireInputId(taskId, "task ID");
        return this.get(`${this.endpoint}/${id}/comments`, (data) => requireTaskPage(data, (item) => {
            const comment = requireTaskComment(item);
            if (comment.taskId !== id) throw new AssessmentTaskApiError("Comment response changed task.");
            return comment;
        }), pageParams(query));
    }

    getAssessmentFilterOptions(query: AssessmentTaskOptionQuery = {}): Observable<AssessmentTaskPage<AssessmentTaskAssessmentOption>> {
        return this.get(`${this.endpoint}/filter-options/assessments`,
            (data) => requireTaskPage(data, requireAssessmentOption), optionParams(query));
    }

    getAssigneeFilterOptions(query: AssessmentTaskOptionQuery = {}): Observable<AssessmentTaskPage<AssessmentTaskAssigneeOption>> {
        return this.get(`${this.endpoint}/filter-options/assignees`,
            (data) => requireTaskPage(data, requireAssigneeOption), optionParams(query));
    }

    getRequirementFilterOptions(
        assessmentId: number, query: AssessmentTaskOptionQuery = {}
    ): Observable<AssessmentTaskPage<AssessmentTaskRequirementOption>> {
        const id = requireInputId(assessmentId, "assessment ID");
        const params = optionParams(query).set("assessmentId", String(id));
        return this.get(`${this.endpoint}/filter-options/requirements`, (data) => requireTaskPage(data, (item) => {
            const option = requireRequirementOption(item);
            if (option.productLicenseId !== id) {
                throw new AssessmentTaskApiError("Requirement option changed assessment.");
            }
            return option;
        }), params);
    }

    getStatusFilterOptions(query: AssessmentTaskOptionQuery = {}): Observable<AssessmentTaskPage<AssessmentTaskStatusOption>> {
        return this.get(`${this.endpoint}/filter-options/statuses`,
            (data) => requireTaskPage(data, requireStatusOption), optionParams(query));
    }

    getCreationOptions(
        assessmentId: number, query: AssessmentTaskCreationOptionsQuery = {}
    ): Observable<AssessmentTaskCreationOptions> {
        const id = requireInputId(assessmentId, "assessment ID");
        return this.get(`${this.endpoint}/creation-options/${id}`, (data) => {
            const options = requireTaskCreationOptions(data);
            if (options.productLicenseId !== id) {
                throw new AssessmentTaskApiError("Task creation options changed assessment.");
            }
            return options;
        }, creationOptionsParams(query));
    }

    getEditOptions(taskId: number, query: AssessmentTaskCreationOptionsQuery = {}): Observable<AssessmentTaskEditOptions> {
        const id = requireInputId(taskId, "task ID");
        return this.get(`${this.endpoint}/${id}/edit-options`, (data) => {
            const options = requireTaskEditOptions(data);
            if (options.taskId !== id) throw new AssessmentTaskApiError("Task edit options changed task.");
            return options;
        }, creationOptionsParams(query));
    }

    getAssessmentCount(assessmentId: number): Observable<AssessmentTaskCount> {
        return this.get(`${this.endpoint}/counts/assessments/${requireInputId(assessmentId, "assessment ID")}`,
            requireTaskCount);
    }

    getAssessmentStatusCounts(assessmentId: number): Observable<Record<AssessmentTaskStatus, number>> {
        return this.get(`${this.endpoint}/counts/assessments/${requireInputId(assessmentId, "assessment ID")}/statuses`,
            requireTaskStatusCounts);
    }

    getMyUnfinishedCount(): Observable<AssessmentTaskCount> {
        return this.get(`${this.endpoint}/counts/me/unfinished`, requireTaskCount);
    }

    updateBrowserTimezone(timezone: string): Observable<{ timezone: string; changed: boolean }> {
        if (typeof timezone !== "string" || !timezone) throw new Error("A browser timezone is required.");
        return this.http.put<AssessmentTaskApiResponse>(`${this.endpoint}/timezone`, { timezone }).pipe(
            map((response) => {
                const data = requireTaskApiData(response, "Unable to save browser timezone.");
                if (!data || typeof data !== "object" || Array.isArray(data)) {
                    throw new Error("Invalid browser timezone response.");
                }
                const result = data as Record<string, unknown>;
                if (result["timezone"] !== timezone || typeof result["changed"] !== "boolean") {
                    throw new Error("Invalid browser timezone response.");
                }
                return { timezone, changed: result["changed"] as boolean };
            })
        );
    }

    createTask(input: AssessmentTaskCreateInput): Observable<AssessmentTaskMutationReceipt> {
        const productLicenseId = requireInputId(input.productLicenseId, "assessment ID");
        return this.http.post<AssessmentTaskApiResponse>(this.endpoint, input).pipe(
            map((response) => requireTaskMutationReceipt(requireTaskApiData(response, "Unable to create task."))),
            tap((receipt) => this.emitMutation(
                receipt.id, productLicenseId, "task", receipt.task?.requirements ?? input.requirements
            ))
        );
    }

    editTask(
        taskId: number, input: AssessmentTaskEditInput, previousTask?: AssessmentTaskSummary
    ): Observable<AssessmentTaskMutationReceipt> {
        const id = requireInputId(taskId, "task ID");
        requireInputRevision(input.revision);
        return this.http.patch<AssessmentTaskApiResponse>(`${this.endpoint}/${id}`, input).pipe(
            map((response) => requireTaskMutationReceipt(requireTaskApiData(response, "Unable to edit task."))),
            tap((receipt) => {
                if (receipt.id !== id) throw new Error("Task edit response changed task.");
                const previous = previousTask?.id === id ? previousTask : null;
                const nextRequirements = receipt.task?.requirements ?? input.requirements ?? null;
                if (receipt.changed) this.emitMutation(
                    id, receipt.task?.productLicenseId ?? previous?.productLicenseId ?? null, "task",
                    previous || nextRequirements
                        ? [...(previous?.requirements ?? []), ...(nextRequirements ?? [])] : null
                );
            })
        );
    }

    changeTaskStatus(
        taskId: number, status: AssessmentTaskStatus, revision: number, previousTask?: AssessmentTaskSummary
    ): Observable<AssessmentTaskMutationReceipt> {
        const id = requireInputId(taskId, "task ID");
        return this.http.patch<AssessmentTaskApiResponse>(`${this.endpoint}/${id}/status`,
            { status, revision: requireInputRevision(revision) }).pipe(
            map((response) => requireTaskMutationReceipt(requireTaskApiData(response, "Unable to change task status."))),
            tap((receipt) => {
                if (receipt.id !== id) throw new Error("Task status response changed task.");
                if (receipt.changed) this.emitMutation(
                    id, receipt.task?.productLicenseId ?? (previousTask?.id === id ? previousTask.productLicenseId : null), "task",
                    receipt.task?.requirements ?? (previousTask?.id === id ? previousTask.requirements : null)
                );
            })
        );
    }

    deleteTask(taskId: number, revision: number, previousTask?: AssessmentTaskSummary): Observable<AssessmentTaskDeleteReceipt> {
        const id = requireInputId(taskId, "task ID");
        return this.http.delete<AssessmentTaskApiResponse>(`${this.endpoint}/${id}`, {
            body: { revision: requireInputRevision(revision) }
        }).pipe(
            map((response) => requireTaskDeleteReceipt(requireTaskApiData(response, "Unable to delete task."))),
            tap((receipt) => {
                if (receipt.id !== id) throw new Error("Task deletion response changed task.");
                this.emitMutation(
                    id, previousTask?.id === id ? previousTask.productLicenseId : null, "delete",
                    previousTask?.id === id ? previousTask.requirements : null
                );
            })
        );
    }

    addComment(
        taskId: number, body: string, taskContext?: AssessmentTaskSummary
    ): Observable<AssessmentTaskCommentMutationReceipt> {
        const id = requireInputId(taskId, "task ID");
        return this.http.post<AssessmentTaskApiResponse>(`${this.endpoint}/${id}/comments`, { body }).pipe(
            map((response) => requireCommentMutationReceipt(requireTaskApiData(response, "Unable to add comment."))),
            tap((receipt) => this.emitCommentMutation(receipt, id, taskContext))
        );
    }

    editComment(
        taskId: number, commentId: number, body: string, revision: number,
        taskContext?: AssessmentTaskSummary
    ): Observable<AssessmentTaskCommentMutationReceipt> {
        const id = requireInputId(taskId, "task ID");
        const selectedCommentId = requireInputId(commentId, "comment ID");
        return this.http.patch<AssessmentTaskApiResponse>(
            `${this.endpoint}/${id}/comments/${selectedCommentId}`,
            { body, revision: requireInputRevision(revision) }
        ).pipe(
            map((response) => requireCommentMutationReceipt(requireTaskApiData(response, "Unable to edit comment."))),
            tap((receipt) => {
                if (receipt.id !== selectedCommentId) throw new Error("Comment edit response changed comment.");
                if (receipt.changed) this.emitCommentMutation(receipt, id, taskContext);
            })
        );
    }

    deleteComment(
        taskId: number, commentId: number, revision: number, taskContext?: AssessmentTaskSummary
    ): Observable<AssessmentTaskCommentDeleteReceipt> {
        const id = requireInputId(taskId, "task ID");
        const selectedCommentId = requireInputId(commentId, "comment ID");
        return this.http.delete<AssessmentTaskApiResponse>(
            `${this.endpoint}/${id}/comments/${selectedCommentId}`,
            { body: { revision: requireInputRevision(revision) } }
        ).pipe(
            map((response) => requireCommentDeleteReceipt(requireTaskApiData(response, "Unable to delete comment."))),
            tap((receipt) => {
                if (receipt.id !== selectedCommentId || receipt.taskId !== id) {
                    throw new Error("Comment deletion response changed target.");
                }
                this.emitCommentChange(id, receipt.taskRevision, taskContext);
            })
        );
    }

    private get<T>(url: string, normalize: (data: unknown) => T, params?: HttpParams): Observable<T> {
        return this.http.get<AssessmentTaskApiResponse>(url, { params }).pipe(
            map((response) => normalize(requireTaskApiData(response, "Unable to load assessment tasks.")))
        );
    }

    private emitCommentMutation(
        receipt: AssessmentTaskCommentMutationReceipt, taskId: number, taskContext?: AssessmentTaskSummary
    ): void {
        if (receipt.taskId !== taskId) throw new Error("Comment response changed task.");
        this.emitCommentChange(taskId, receipt.taskRevision, taskContext);
    }

    private emitCommentChange(taskId: number, taskRevision: number, taskContext?: AssessmentTaskSummary): void {
        const task = taskContext?.id === taskId && taskContext.revision + 1 === taskRevision
            ? taskContext : null;
        this.emitMutation(taskId, task?.productLicenseId ?? null, "comment", task?.requirements ?? null);
    }

    private emitMutation(
        taskId: number,
        productLicenseId: number | null,
        kind: AssessmentTaskMutationEvent["kind"],
        affectedRequirements: readonly AssessmentTaskRequirementRef[] | null = null
    ): void {
        this.mutationEvents.next({ taskId, productLicenseId, kind, affectedRequirements });
    }
}
